// react
import { ChangeEvent, useContext, useEffect, useState } from 'react';

// types
import { DocumentType, ExpenseType, Transaction } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextUser } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// ui
import { IconCircle, transactionIconTypes } from '../../ui/IconCircle/IconCircle';
import { Loading } from '../../ui/Loading/Loading';

// cards
import { CardUploadFile } from '../../cards/CardUploadFile/CardUploadFile';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';
import { postUploader } from '../../../utils/files';

// theme
import { theme } from '../../../theme';

// props
export interface DialogTransactionProps {
	isOpen: boolean;
	onClose: () => void;
	onUpload: (e: ChangeEvent<HTMLInputElement>, response: any) => void;
	transaction: Transaction | undefined;
}

export const DialogTransaction = ({
	isOpen,
	onClose,
	onUpload,
	transaction,
}: DialogTransactionProps) => {
	// context
	const { user } = useContext(ContextUser);

	// hooks
	const { t } = useTranslation();

	// session
	const token = sessionStorage.getItem('token');

	// state
	const [docTypes, setDocTypes] = useState<DocumentType[]>([]);

	// vars
	const expenseType = transaction?.expenseType as ExpenseType;

	// fetch document types
	const { fetchRequest: fetchDocumentTypes, isLoading: isLoadingDocs } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/api/proxies/document-types?expenseType=${expenseType?.id}`,
		options: {
			headers: {
				Authorization: `Bearer ${token}`,
				apikey: process.env.REACT_APP_APIKEY || '',
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocTypes(res.data);
			}
		},
	});

	// handlers
	const handleClickCard = async (e: ChangeEvent<HTMLInputElement>, docType: DocumentType) => {
		const { files } = e.target as HTMLInputElement;

		if (!files || !user || !transaction) {
			return;
		}

		const reqPostUploader = await postUploader({
			docType,
			expenseType,
			file: files[0],
			transaction,
			user,
		});

		if (onUpload) {
			onUpload(e, reqPostUploader);
		}
	};

	const renderContent = () => {
		if (isLoadingDocs) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading CircularProgressProps={{ color: 'inherit' }} />
						</Grid>
					</Grid>
				</Grid>
			);
		} else if (docTypes.length) {
			return docTypes.map((docType, index) => {
				return (
					<Grid item key={`document-upload-${index}`} xs={12}>
						<CardUploadFile
							onUpload={(e) => handleClickCard(e, docType)}
							IconCircleProps={{ color: 'primary', scheme: 'dark', type: 'upload' }}
							label={docType.name}
						/>
					</Grid>
				);
			});
		}

		return (
			<Grid item xs={12}>
				<Alert severity="info" variant="outlined">
					{t('alert.noDocumentsRequired')}
				</Alert>
			</Grid>
		);
	};

	// fetch document types when transaction changes
	useEffect(() => {
		if (transaction) {
			fetchDocumentTypes();
		}
	}, [fetchDocumentTypes, transaction]);

	if (!transaction) {
		return <></>;
	}

	return (
		<DialogActions
			hasClose={true}
			isOpen={isOpen}
			onClose={onClose}
			fullScreen={true}
			PaperProps={{
				sx: {
					backgroundColor: theme.palette.brand.blue[800],
					color: theme.palette.common.white,
				},
			}}>
			<Grid alignItems="center" container spacing={2}>
				<Grid item xs={12}>
					<Grid alignItems="center" container direction="column" spacing={2}>
						<Grid item>
							<IconCircle type={transactionIconTypes[transaction.type]} size="lg" />
						</Grid>
						<Grid item sx={{ textAlign: 'center' }}>
							<Typography variant="h2" fontWeight={600} color="common.white">
								{formatCurrency({ currency: transaction.currency, value: transaction.amount })}
							</Typography>
							<Typography color="brand.blue.400" fontWeight={600}>
								{formatDate({ date: new Date(transaction.date) })}
							</Typography>
						</Grid>
					</Grid>
				</Grid>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
